<script>
  import {
    SEARCH_RARITY_OPTIONS,
    SEARCH_FINISH_OPTIONS,
    SEARCH_FRAME_OPTIONS,
  } from '../../lib/logic/config.js';

  /**
   * @type {{
   *   filters?: Object,
   *   isDirty?: boolean,
   *   hideMyCards?: boolean,
   *   showHideMyCards?: boolean,
   *   onFiltersChange: (next: Object) => void,
   *   onApply: () => void,
   *   onHideMyCardsChange?: (checked: boolean) => void,
   * }}
   */
  let {
    filters = {},
    isDirty = false,
    hideMyCards = false,
    showHideMyCards = false,
    onFiltersChange,
    onApply,
    onHideMyCardsChange,
  } = $props();

  const COLORS = [
    { code: 'W', label: 'White', svg: '/mana/W.svg' },
    { code: 'U', label: 'Blue', svg: '/mana/U.svg' },
    { code: 'B', label: 'Black', svg: '/mana/B.svg' },
    { code: 'R', label: 'Red', svg: '/mana/R.svg' },
    { code: 'G', label: 'Green', svg: '/mana/G.svg' },
    { code: 'C', label: 'Colorless', svg: '/mana/C.svg' },
  ];

  function toggleColor(code) {
    const current = filters.colors || [];
    // Colorless is exclusive — clear others when toggling C, and clear C when toggling a color
    if (code === 'C') {
      const next = current.includes('C') ? [] : ['C'];
      onFiltersChange({ ...filters, colors: next });
      return;
    }
    const withoutC = current.filter((c) => c !== 'C');
    const next = withoutC.includes(code) ? withoutC.filter((c) => c !== code) : [...withoutC, code];
    onFiltersChange({ ...filters, colors: next });
  }

  function setRarity(r) {
    onFiltersChange({ ...filters, rarity: filters.rarity === r ? '' : r });
  }

  function setFinish(f) {
    onFiltersChange({ ...filters, finish: filters.finish === f ? '' : f });
  }

  function toggleFrame(frameValue) {
    const current = filters.frames || [];
    const next = current.includes(frameValue)
      ? current.filter((f) => f !== frameValue)
      : [...current, frameValue];
    onFiltersChange({ ...filters, frames: next });
  }

  function handleTypeInput(e) {
    onFiltersChange({ ...filters, type_line: e.target.value });
  }

  function handleSetInput(e) {
    const val = e.target.value.trim();
    onFiltersChange({
      ...filters,
      set_codes: val ? val.split(/[,\s]+/).filter(Boolean) : [],
    });
  }

  function handleMinCmc(e) {
    const val = e.target.value;
    onFiltersChange({ ...filters, min_cmc: val !== '' ? parseInt(val, 10) : null });
  }

  function handleMaxCmc(e) {
    const val = e.target.value;
    onFiltersChange({ ...filters, max_cmc: val !== '' ? parseInt(val, 10) : null });
  }

  function clearAll() {
    onFiltersChange({
      type_line: '',
      colors: [],
      rarity: '',
      min_cmc: null,
      max_cmc: null,
      set_codes: [],
      finish: '',
      frames: [],
    });
  }

  function handleKeydown(e) {
    if (e.key === 'Enter') {
      onApply();
    }
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div class="filter-panel" role="region" aria-label="Search filters" onkeydown={handleKeydown}>
  <!-- Colors -->
  <div class="filter-section" role="group" aria-labelledby="filter-color-label">
    <span id="filter-color-label" class="filter-label">Color</span>
    <div class="color-buttons">
      {#each COLORS as color (color.code)}
        <button
          class="color-btn"
          class:active={(filters.colors || []).includes(color.code)}
          title={color.label}
          onclick={() => toggleColor(color.code)}
        >
          <img src={color.svg} alt={color.label} width="20" height="20" />
        </button>
      {/each}
    </div>
  </div>

  <!-- Type -->
  <div class="filter-section">
    <label class="filter-label" for="filter-type">Type</label>
    <input
      id="filter-type"
      class="filter-input"
      type="text"
      placeholder="e.g. Creature, Elf, Instant"
      value={filters.type_line || ''}
      oninput={handleTypeInput}
    />
  </div>

  <!-- Rarity -->
  <div class="filter-section" role="group" aria-labelledby="filter-rarity-label">
    <span id="filter-rarity-label" class="filter-label">Rarity</span>
    <div class="chip-group">
      {#each SEARCH_RARITY_OPTIONS as r (r)}
        <button
          class="chip rarity-{r}"
          class:active={filters.rarity === r}
          onclick={() => setRarity(r)}
        >
          {r[0].toUpperCase() + r.slice(1)}
        </button>
      {/each}
    </div>
  </div>

  <!-- CMC Range -->
  <div class="filter-section filter-section-row" role="group" aria-labelledby="filter-cmc-label">
    <span id="filter-cmc-label" class="filter-label">CMC</span>
    <div class="cmc-range">
      <input
        class="filter-input cmc-input"
        type="number"
        min="0"
        max="20"
        placeholder="Min"
        value={filters.min_cmc ?? ''}
        oninput={handleMinCmc}
      />
      <span class="cmc-separator">&ndash;</span>
      <input
        class="filter-input cmc-input"
        type="number"
        min="0"
        max="20"
        placeholder="Max"
        value={filters.max_cmc ?? ''}
        oninput={handleMaxCmc}
      />
    </div>
  </div>

  <!-- Set Code -->
  <div class="filter-section">
    <label class="filter-label" for="filter-set">Set</label>
    <input
      id="filter-set"
      class="filter-input"
      type="text"
      placeholder="e.g. MH2, ONE"
      value={(filters.set_codes || []).join(' ')}
      oninput={handleSetInput}
    />
  </div>

  <!-- Finish -->
  <div class="filter-section" role="group" aria-labelledby="filter-finish-label">
    <span id="filter-finish-label" class="filter-label">Finish</span>
    <div class="chip-group">
      {#each SEARCH_FINISH_OPTIONS as f (f)}
        <button class="chip" class:active={filters.finish === f} onclick={() => setFinish(f)}>
          {f[0].toUpperCase() + f.slice(1)}
        </button>
      {/each}
    </div>
  </div>

  <!-- Frame -->
  <div class="filter-section" role="group" aria-labelledby="filter-frame-label">
    <span id="filter-frame-label" class="filter-label">Frame</span>
    <div class="chip-group">
      {#each SEARCH_FRAME_OPTIONS as frame (frame.value)}
        <button
          class="chip"
          class:active={(filters.frames || []).includes(frame.value)}
          onclick={() => toggleFrame(frame.value)}
        >
          {frame.label}
        </button>
      {/each}
    </div>
  </div>

  <!-- Hide my cards toggle (group mode only) -->
  {#if showHideMyCards}
    <div class="filter-section filter-section-toggle">
      <label class="toggle-label" for="hide-my-cards">
        <input
          id="hide-my-cards"
          type="checkbox"
          class="toggle-input"
          checked={hideMyCards}
          onchange={(e) => onHideMyCardsChange(e.currentTarget.checked)}
        />
        <span class="toggle-text">Hide my cards</span>
      </label>
    </div>
  {/if}

  <!-- Actions -->
  <div class="filter-actions">
    <button class="filter-clear-btn" onclick={clearAll}>Clear All</button>
    <button class="filter-apply-btn" class:dirty={isDirty} onclick={onApply}>
      {isDirty ? 'Apply Changes' : 'Apply'}
    </button>
  </div>
</div>

<style>
  .filter-panel {
    background: var(--color-bg-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: 1rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem 1.5rem;
    animation: filter-slide-down 0.2s ease-out;
  }

  @keyframes filter-slide-down {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .filter-section {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .filter-section-row {
    flex-direction: column;
  }

  .filter-label {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .filter-input {
    padding: 0.375rem 0.625rem;
    border: 1px solid var(--color-border-subtle);
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-family: inherit;
    outline: none;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
    width: 100%;
    box-sizing: border-box;
  }

  .filter-input:focus {
    border-color: var(--color-text-tertiary);
    box-shadow: 0 0 0 2px rgba(154, 142, 124, 0.1);
  }

  .color-buttons {
    display: flex;
    gap: 0.25rem;
  }

  .color-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid var(--color-border);
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition:
      border-color 0.15s,
      box-shadow 0.15s,
      transform 0.1s;
  }

  .color-btn:hover {
    border-color: var(--color-text-muted);
    transform: scale(1.08);
  }

  .color-btn.active {
    border-color: var(--color-accent-dark);
    box-shadow: 0 0 0 2px rgba(44, 36, 25, 0.15);
  }

  .color-btn img {
    border-radius: 50%;
  }

  .chip-group {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .chip {
    padding: 0.25rem 0.625rem;
    border: 1px solid var(--color-border-subtle);
    border-radius: 9999px;
    background: var(--color-bg-surface);
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s,
      color 0.15s;
  }

  .chip:hover {
    background: var(--color-accent-bg);
    border-color: var(--color-text-muted);
  }

  .chip.active {
    background: var(--color-accent-bg);
    border-color: var(--color-accent-dark);
    color: var(--color-text);
  }

  /* Rarity-specific chip colors when active */
  .chip.rarity-common.active {
    background: #f3f4f6;
    border-color: #6b7280;
    color: #374151;
  }

  .chip.rarity-uncommon.active {
    background: #f0f0f0;
    border-color: #71717a;
    color: #52525b;
  }

  .chip.rarity-rare.active {
    background: #fef9ee;
    border-color: #b8860b;
    color: #92700e;
  }

  .chip.rarity-mythic.active {
    background: #fff1eb;
    border-color: #cc3700;
    color: #b83000;
  }

  .cmc-range {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .cmc-input {
    width: 64px;
    text-align: center;
  }

  .cmc-separator {
    color: var(--color-text-muted);
    font-size: 0.875rem;
  }

  .filter-section-toggle {
    display: flex;
    align-items: center;
    grid-column: 1 / -1;
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.8125rem;
    color: var(--color-text);
  }

  .toggle-input {
    accent-color: var(--color-accent-dark);
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .filter-actions {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    grid-column: 1 / -1;
    justify-content: flex-end;
  }

  .filter-clear-btn {
    padding: 0.375rem 0.75rem;
    border: 1px solid var(--color-border-subtle);
    border-radius: 0.375rem;
    background: white;
    font-size: 0.8125rem;
    color: var(--color-text-tertiary);
    cursor: pointer;
    transition: background 0.15s;
  }

  .filter-clear-btn:hover {
    background: var(--color-accent-bg);
  }

  .filter-apply-btn {
    padding: 0.375rem 1rem;
    border: none;
    border-radius: 0.375rem;
    background: var(--color-accent-dark);
    color: var(--color-bg);
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
  }

  .filter-apply-btn:hover {
    background: var(--color-accent-hover);
  }

  .filter-apply-btn.dirty {
    animation: pulse-apply 1.5s ease-in-out infinite;
    box-shadow: 0 0 0 2px rgba(44, 36, 25, 0.2);
  }

  @keyframes pulse-apply {
    0%,
    100% {
      box-shadow: 0 0 0 2px rgba(44, 36, 25, 0.2);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(44, 36, 25, 0.1);
    }
  }

  @media (max-width: 768px) {
    .filter-panel {
      grid-template-columns: 1fr 1fr;
      gap: 0.625rem 0.75rem;
    }

    .filter-section:has(.color-buttons) {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 480px) {
    .filter-panel {
      grid-template-columns: 1fr;
    }
  }
</style>
